import {ReactNode, ReactElement, useState} from 'react'
import {NavLink} from 'react-router-dom'
import Box from '@mui/material/Box'
import Checkbox from '@mui/material/Checkbox'
import CircularProgress from '@mui/material/CircularProgress'
import Dialog from '@mui/material/Dialog'
import DialogActions from '@mui/material/DialogActions'
import DialogContent from '@mui/material/DialogContent'
import DialogContentText from '@mui/material/DialogContentText'
import DialogTitle from '@mui/material/DialogTitle'
import ScheduleIcon from '@mui/icons-material/Schedule'
import Stack from '@mui/material/Stack'
import Table from '@mui/material/Table'
import TableBody from '@mui/material/TableBody'
import TableCell, {tableCellClasses} from '@mui/material/TableCell'
import TableContainer from '@mui/material/TableContainer'
import TableHead from '@mui/material/TableHead'
import TableRow from '@mui/material/TableRow'
import Tooltip from '@mui/material/Tooltip'
import Typography from '@mui/material/Typography'
import {styled} from '@mui/material/styles'
import useUser from '../../hooks/useUser'
import {RoundedButton, RedirectButton, NavButton, SelectElem} from '../../components/forms/inputs'
import {titleStyle} from '../../components/text'
import {HelpTooltip} from '../../components/filters/widgets'
import Searcher, {SearchOptions} from '../../components/search/Searcher'
import {connectModels} from '../../services/evaluation'
import Path from '../../routeLinks'

import type {FilterConfig, SearchResult} from '../../components/filters'
import type {
  Evaluation as EvaluationType,
  Model as ModelType,
  ID,
  Cadence,
  ItemType,
} from '../../services/types'

type Item = EvaluationType | ModelType
type Connection = ModelType & {
  price?: number
  cadence?: Cadence
}
type Connections = {
  [k: ID]: Connection
}

type OnPaidDialogProps = {
  item: EvaluationType
  open?: boolean
  selected?: Connection[]
  onClose: () => void
}
const OnPaidDialog = ({item, onClose, open, selected}: OnPaidDialogProps) =>
  selected ? (
    <Dialog open={!!open} onClose={onClose}>
      <DialogContent sx={{width: 550}}>
        <Typography sx={{mt: 3, mb: 1}}>
          Your evaluation{selected.length > 1 ? 's have' : ' has'} been started - check the{' '}
          <NavLink to={Path.evaluations.runs(item.id)}>reports page</NavLink> to see how{' '}
          {selected.length > 1 ? 'they are' : 'it is'} doing
        </Typography>
      </DialogContent>
      <DialogActions>
        <RoundedButton onClick={onClose} label="Close" />
      </DialogActions>
    </Dialog>
  ) : null

type ConfirmDialogProps = {
  itemType: ItemType
  open?: boolean
  selected?: Connection[]
  onConfirm: () => Promise<string>
  onCancel: () => void
}
const ConfirmDialog = ({itemType, open, selected, onCancel, onConfirm}: ConfirmDialogProps) => {
  const [cancelable, setCancelable] = useState(true)

  const confirm = async () => {
    setCancelable(false)
    return onConfirm()
  }

  const Price = ({name, price, variant}: {name: string; price?: number; variant?: any}) => (
    <Stack key={name} direction="row" justifyContent="space-between">
      <DialogContentText variant={variant} sx={{width: 500}}>
        {name}
      </DialogContentText>
      <DialogContentText variant={variant}>${(price || 1).toFixed(2)}</DialogContentText>
    </Stack>
  )

  return selected ? (
    <Dialog open={!!open} onClose={onCancel}>
      <DialogTitle>
        The following{' '}
        {itemType === 'evaluation' ? 'models will be evaluated:' : 'evaluations will be run'}
      </DialogTitle>
      <DialogContent sx={{width: 550}}>
        {selected.map(Price)}
        <Typography sx={{mt: 3, mb: 1}}>
          {itemType === 'evaluation'
            ? 'The total cost to evaluate all of these models once is:'
            : 'The total cost to run all these evaluations is:'}
        </Typography>
        <Price
          name="Total"
          price={selected.reduce((sum, {price}) => sum + (price || 1), 0)}
          variant="h5"
        />
      </DialogContent>
      <DialogActions>
        {cancelable ? <RoundedButton onClick={onCancel} label="Cancel" /> : undefined}
        <RedirectButton label="Run evaluations" urlGetter={confirm} />
      </DialogActions>
    </Dialog>
  ) : null
}

const HeaderCell = styled(TableCell)(({theme}) => ({
  [`&.${tableCellClasses.head}`]: {
    ...titleStyle,
    color: theme.palette.primary.main,
    backgroundColor: theme.palette.secondary.dark,
    border: `2px solid ${theme.palette.primary.main}`,
  },
}))

const StyledTableCell = styled(TableCell)(({theme}) => ({
  [`&.${tableCellClasses.body}`]: {
    border: `2px solid ${theme.palette.primary.main}`,
  },
}))

const capitalize = (word: string) => word.charAt(0).toUpperCase() + word.slice(1).toLowerCase()

const itemsTable =
  (itemType: ItemType) =>
  ({children}: {children: ReactNode}) => {
    if (Array.isArray(children)) {
      const child = children[1]
      if (child && typeof child === 'object' && (child as {key: any}).key === 'no-items') {
        return child as ReactElement
      }
    }

    return (
      <TableContainer>
        <Table sx={{minWidth: 650}} aria-label="sample questions">
          <TableHead>
            <TableRow>
              <HeaderCell sx={{width: 20}}> </HeaderCell>
              <HeaderCell sx={{fontWeight: 700}}>{capitalize(itemType)}</HeaderCell>
              <HeaderCell sx={{fontWeight: 700, width: 20}}>Status</HeaderCell>
              <HeaderCell align="left" sx={{fontWeight: 700, width: 120}}>
                cost{' '}
                <HelpTooltip help="The total cost of running this evaluation on this model, in USD. This price is the same each time the evaluation is run - if the model or evaluation change drastically, and so the execution cost changes a lot, we will present you with a new price which you will have to accept before we run any additional evaluations. This way you know exactly how much you'll be charged and won't get hit with hidden price hikes" />
              </HeaderCell>
            </TableRow>
          </TableHead>
          <TableBody>{children}</TableBody>
        </Table>
      </TableContainer>
    )
  }

const RunStatus = ({item}: {item: Item}) => {
  if (item.next_run === 'now') {
    return (
      <Tooltip title="An evaluation is currently running for this model">
        <CircularProgress />
      </Tooltip>
    )
  } else if (item.next_run) {
    return (
      <Tooltip title={`This model is scheduled to be evaluated ${item.next_run}`}>
        <ScheduleIcon />
      </Tooltip>
    )
  }
  return null
}

type ItemRowProps = {
  item: ModelType | EvaluationType
  onSelect: (item: Item, selected: boolean) => void
  selected?: boolean
  itemType: ItemType
}
const ItemRow = ({item, itemType, onSelect, selected}: ItemRowProps) => {
  const to = itemType === 'model' ? Path.models.show(item.id) : Path.evaluations.show(item.id)
  return (
    <TableRow onClick={() => onSelect(item, !selected)}>
      <StyledTableCell component="th" scope="row">
        <Checkbox checked={selected} onChange={(_, val: boolean) => onSelect(item, val)} />
      </StyledTableCell>
      <StyledTableCell component="th" scope="row">
        <NavButton label={item.name} to={to} />
      </StyledTableCell>
      <StyledTableCell component="th" scope="row">
        <RunStatus item={item} />
      </StyledTableCell>
      <StyledTableCell align="right">
        <Typography align="right">${item.price?.toFixed(2)}</Typography>
      </StyledTableCell>
    </TableRow>
  )
}

const CONFIRMATION = 'confirm'
const AFTER_PAYMENT = 'after payment'
type ConnectModelsProps = {
  item: EvaluationType
  itemType: ItemType
  searchOptions: SearchOptions
  itemsGetter: (
    params: FilterConfig,
    transform?: (item: ModelType | EvaluationType) => Item
  ) => Promise<SearchResult>
}
const ConnectModels = ({item, itemType, searchOptions, itemsGetter}: ConnectModelsProps) => {
  const [shownDialog, setShownDialog] = useState('')
  const [selected, setSelected] = useState({} as Connections)
  const [cadence, setCadence] = useState<Cadence>('once')
  const {user} = useUser()
  const connectedType = itemType === 'model' ? 'evaluation' : 'model'

  const save = async () => {
    if (!user) return

    const connections = Object.values(selected).map(({id, name, price}) => ({
      evaluatee_id: itemType === 'model' ? item.id : id,
      evaluation_id: itemType === 'model' ? id : item.id,
      name: itemType === 'model' ? `${name}: ${item.name}` : `${item.name}: ${name}`,
      cadence: cadence || 'weekly',
      price: price || 1,
    }))
    const {checkout_url} = await connectModels(connections)
    setSelected({})
    setShownDialog(AFTER_PAYMENT)
    return checkout_url
  }

  const toggleItem = (item: Item, selected: boolean) => {
    if (selected) {
      setSelected((prev) => ({...prev, [item.id]: item}))
    } else {
      setSelected((prev) => {
        delete prev[item.id]
        return {...prev}
      })
    }
  }

  // Make sure each model has the price added - this makes it a lot easier to sort
  const getItems = async (params: FilterConfig) =>
    itemsGetter(params, (item: Item) => ({
      ...item,
      price: Math.max(item.price ? item.price / 100 : 1, 1),
    }))

  return (
    <Stack direction="column" sx={{pt: 5, pb: 10}} spacing={3}>
      <Typography variant="h4" sx={{color: 'primary.main'}}>
        Choose which {connectedType}s to evaluate:
      </Typography>
      <Searcher
        getItems={getItems}
        renderItem={(item: Item) => (
          <ItemRow
            key={item.id}
            onSelect={toggleItem}
            item={item}
            itemType={connectedType}
            selected={!!selected[item.id]}
          />
        )}
        searchOptions={searchOptions}
        itemsContainer={itemsTable(connectedType)}
      />
      <Stack
        direction={{md: 'row', sm: 'column'}}
        justifyContent="flex-end"
        alignItems="center"
        spacing={2}
        sx={{p: 2}}
      >
        <RoundedButton
          onClick={() => setShownDialog(CONFIRMATION)}
          disabled={Object.keys(selected).length === 0}
          sx={{minWidth: 160}}
          label={`Connect ${itemType}s`}
        />
        <SelectElem
          defaultValue={cadence}
          label="Cadence"
          alwaysSelected
          onChange={setCadence}
          values={['once', 'daily', 'weekly', 'every 2 weeks', 'monthly', 'quarterly'].reduce(
            (acc, v) => ({...acc, [v]: v}),
            {}
          )}
        />
        <Box sx={{width: '100%'}} />
        <Typography variant="h5" sx={{minWidth: 130}}>
          Total cost:
        </Typography>
        <Typography variant="h5" align="right" sx={{width: 100}}>
          $
          {Object.values(selected)
            .map(({price}) => price || 1)
            .reduce((a, b) => a + b, 0)
            .toFixed(2)}
        </Typography>
      </Stack>
      <ConfirmDialog
        itemType={itemType}
        open={shownDialog === CONFIRMATION}
        selected={selected && Object.values(selected)}
        onCancel={() => setShownDialog('')}
        onConfirm={save}
      />
      <OnPaidDialog
        item={item}
        open={shownDialog === AFTER_PAYMENT}
        selected={selected && Object.values(selected)}
        onClose={() => setShownDialog('')}
      />
    </Stack>
  )
}

export default ConnectModels
